import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { useSettings } from '../../../../hooks';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Markdown from '../../../ManagementPanel/components/Markdown';
import { formatAsDollar } from '../../../PolicyStream/utility';
import { graphql } from '../../../../gql';
import { useMutation } from '@apollo/client';
import { PremiumProposal } from '../../../../Typescript/backend/classes/PremiumProposal';
import { CircularProgress, styled } from '@mui/material';
import { safelyGetAttributes } from '../../../../util';

// types and classes
import type { StoreState } from '../../../../store/';
// import { Policy } from '../../../../Typescript';
import type { $TSFixMe } from '@calefy-inc/utilityTypes';

const getPremiumProposals = (store: StoreState) =>
  store.quoteWizard.premiumProposals
    .filter((proposal) => proposal.status !== 'REJECTED')
    .map(PremiumProposal.generateFromBackendResponse)
    .sort((a, b) => a.amountInCents - b.amountInCents);

const getSubmittedPolicies = (store: StoreState) => {
  return Object.values(store.quoteWizard.formAnswers)
    .map((formAnswer) => formAnswer.form.policy)
    .filter((policy) => !!policy);
};

// const DISPLAY_DIFFERENCE_THRESHOLD = 10; // minimum difference between the min and max to display different premiums; if the difference is less than this amount than *only* the minimum is displayed.

export const formatForListDisplay = (strings: Array<string>): string => {
  if (strings.length === 0) {
    return '';
  }
  if (strings.length === 1) {
    return strings[0];
  }
  if (strings.length === 2) {
    return strings.join(' or ');
  }
  return (
    strings.slice(0, -2).join(', ') + ', ' + strings.slice(-2).join(', or ')
  );
};

interface PremiumProposalsDisplayProps {
  classes: $TSFixMe;
  proposalSelected: boolean;
  setProposalSelected: Dispatch<SetStateAction<boolean>>;
}
export const PremiumProposalsDisplay = ({
  classes,
  proposalSelected,
  setProposalSelected,
}: PremiumProposalsDisplayProps) => {
  // const { slug } = useSettings();
  const premiumProposals = useSelector(getPremiumProposals);
  const quoteUUID = useSelector(
    (store: StoreState) => store.quoteWizard.currentQuoteUUID,
  );
  const quoteFriendlyId = useSelector(
    (store: StoreState) =>
      store.quoteWizard.submittedQuotes[0]?.friendlyId ??
      store.quoteWizard.submittedQuotes[0]?.uniqueID ??
      null,
  );

  const submittedPolicies = useSelector(getSubmittedPolicies);
  const [min, setMin] = useState<null | number>(null);
  const [max, setMax] = useState<null | number>(null);
  // const [uncoveredPolicies, setUncoveredPolicies] = useState<Array<Policy>>([]);
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (premiumProposals && premiumProposals.length > 0) {
      const allPremiumAmounts = premiumProposals.map(
        (proposal) => proposal.amountInCents / 100,
      );
      setMin(Math.min(...allPremiumAmounts));
      setMax(Math.max(...allPremiumAmounts));
    }
  }, [premiumProposals]);

  // set the policies which are not covered by the premium proposals (NB This is very rough - doesn't account at all for the fact that different proposals might overlap the policies in different ways. Treat with caution!)
  useEffect(() => {
    if (
      submittedPolicies.length > 0 &&
      premiumProposals.filter((proposal) => proposal.status !== 'REJECTED')
        .length > 0
    ) {
      // const proposalPolicyInternalNames = premiumProposals
      //   .reduce(
      //     (allPolicies: Array<Policy>, proposal) => [
      //       ...allPolicies,
      //       ...proposal.policies,
      //     ],
      //     [],
      //   )
      //   .filter((policy) => !!policy)
      //   .map((proposal) => proposal.internalName);
      // setUncoveredPolicies(
      //   // @ts-expect-error
      //   submittedPolicies.filter(
      //     (quotePolicy) =>
      //       // @ts-expect-error
      //       !proposalPolicyInternalNames.includes(quotePolicy.internalName),
      //   ),
      // );
    }
  }, [premiumProposals, submittedPolicies]);

  if (
    !premiumProposals ||
    premiumProposals.length === 0 ||
    // if all of the costs are 0, something is wrong -> ignore it
    premiumProposals.filter((proposal) => proposal.amountInCents > 0).length ===
      0 ||
    premiumProposals.filter((proposal) => proposal.status !== 'REJECTED')
      .length === 0 ||
    min === null ||
    max === null ||
    submittedPolicies.length === 0
  ) {
    return null;
  }
  return (
    <>
      {!proposalSelected && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            {premiumProposals.map((proposal) => {
              return proposal.amountInCents > 0 ? (
                <ProposalCard
                  proposal={proposal}
                  setProposalSelected={setProposalSelected}
                  quoteUUID={quoteUUID}
                  setIsSubmitting={setIsSubmitting}
                />
              ) : null;
            })}
          </Box>
        </>
      )}
      {proposalSelected && (
        <>
          {isSubmitting ? (
            <SubmittingContainer>
              <Typography
                className={classes.content}
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '2em',
                }}
              >
                Submitting...
              </Typography>
              <CircularProgress />
            </SubmittingContainer>
          ) : (
            <>
              <Typography
                className={classes.content}
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '2em',
                }}
              >
                Your proposal is on its way!
              </Typography>
              <Typography
                className={classes.content}
                sx={{ textAlign: 'center' }}
              >
                We've forwarded your selected insurance proposal to a licensed
                agent. Expect a call shortly from
                <a
                  href={`tel:${settings.phone.replace(/[^+\d]/g, '')}`}
                  style={{
                    fontWeight: 'bold',
                    color: 'inherit',
                    textDecoration: 'underline',
                    marginLeft: 4,
                  }}
                >
                  {settings.phone}
                </a>{' '}
                to review your options and guide you through next steps.
              </Typography>
              <Typography
                className={classes.content}
                sx={{ textAlign: 'center' }}
              >
                Prefer to take the lead? You can also reach us directly at
                <a
                  href='tel:18004253339'
                  style={{
                    fontWeight: 'bold',
                    color: 'inherit',
                    textDecoration: 'underline',
                    marginLeft: 4,
                  }}
                >
                  {settings.phone}
                </a>
                . Just provide your reference number:{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {quoteFriendlyId || quoteUUID}
                </span>
                , and one of our friendly specialists will be happy to assist
                you.
              </Typography>
            </>
          )}
        </>
      )}
    </>
  );
};

const SubmittingContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const ProposalCard = ({
  proposal,
  setProposalSelected,
  quoteUUID,
  setIsSubmitting,
}: {
  proposal: PremiumProposal;
  setProposalSelected: Dispatch<SetStateAction<boolean>>;
  quoteUUID: string | null;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
}) => {
  const [displayProposalModal, setDisplayProposalModal] =
    useState<boolean>(false);
  const [cglLimit, setCglLimit] = useState<string | null>(null);
  const [buildingLimit, setBuildingLimit] = useState<string | null>(null);
  const [businessInterruptionLimit, setBusinessInterruptionLimit] = useState<
    string | null
  >(null);
  const [deductible, setDeductible] = useState<string | null>(null);
  const [premium, setPremium] = useState<string | null>(null);
  const [sendEmailWithPremiumProposal] = useMutation(
    SEND_EMAIL_WITH_PREMIUM_PROPOSAL,
  );

  useEffect(() => {
    const cglLimit = safelyGetAttributes(proposal.additionalInformation, [
      'cgl_limit',
    ]);
    const buildingLimit = safelyGetAttributes(proposal.additionalInformation, [
      'business_property_limits',
    ]);
    const businessInterruptionLimit = safelyGetAttributes(
      proposal.additionalInformation,
      ['business_interruption_limit'],
    );
    const deductible = safelyGetAttributes(proposal.additionalInformation, [
      'deductible',
    ]);
    const premium = safelyGetAttributes(proposal.additionalInformation, [
      'premium',
    ]);

    if (cglLimit) {
      const cglLimitNumber = Number(cglLimit);
      setCglLimit(
        Number.isFinite(cglLimitNumber) ? formatAsDollar(cglLimitNumber) : '',
      );
    }
    if (buildingLimit) {
      const buildingLimitNumber = Number(buildingLimit);
      setBuildingLimit(
        Number.isFinite(buildingLimitNumber)
          ? formatAsDollar(buildingLimitNumber)
          : '',
      );
    }
    if (businessInterruptionLimit) {
      const businessInterruptionLimitNumber = Number(businessInterruptionLimit);
      setBusinessInterruptionLimit(
        Number.isFinite(businessInterruptionLimitNumber)
          ? formatAsDollar(businessInterruptionLimitNumber)
          : '',
      );
    }
    if (deductible) {
      const deductibleNumber = Number(deductible);
      setDeductible(
        Number.isFinite(deductibleNumber)
          ? formatAsDollar(deductibleNumber)
          : '',
      );
    }
    if (premium) {
      const premiumNumber = Number(premium);
      setPremium(
        Number.isFinite(premiumNumber) ? formatAsDollar(premiumNumber) : '',
      );
    }
  }, [proposal]);

  return (
    <>
      <ProposalModal
        open={displayProposalModal}
        proposal={proposal}
        setDisplayProposalModal={setDisplayProposalModal}
      />
      <StyledProposalCard
        sx={{
          border: `3px solid ${getBorderColor(
            proposal.identifier.toLowerCase(),
          )}`,
          '&:hover': {
            boxShadow: 8,
            borderColor: getBorderColor(proposal.identifier.toLowerCase()),
          },
          boxShadow: 4,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography
            gutterBottom
            sx={{
              color: getBorderColor(proposal.identifier.toLowerCase()),
              fontSize: 24,
            }}
          >
            {proposal.identifier.toUpperCase()} Proposal
          </Typography>
          <Typography variant='h6' component='div'>
            {proposal.insurer != null ? proposal?.insurer?.name : 'Calefy'}
          </Typography>

          {cglLimit && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                CGL Limit:
              </CardInfo>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                {cglLimit}
              </CardInfo>
            </div>
          )}
          {buildingLimit && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                Building Limit:
              </CardInfo>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                {buildingLimit}
              </CardInfo>
            </div>
          )}
          {businessInterruptionLimit && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                Business Interruption Limit:
              </CardInfo>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                {businessInterruptionLimit}
              </CardInfo>
            </div>
          )}
          {deductible && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                Deductible:
              </CardInfo>
              <CardInfo sx={{ color: 'text.secondary', mb: 0.5 }}>
                {deductible}
              </CardInfo>
            </div>
          )}

          <br></br>
          <Typography variant='body2' sx={{ fontSize: 16 }}>
            {getDescription(proposal.identifier)}
          </Typography>
          <Button
            size='small'
            sx={{ m: 0, minWidth: 0 }}
            onClick={() => setDisplayProposalModal(true)}
          >
            Details
          </Button>
        </CardContent>
        <BottomProposalCardSection>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {premium && (
              <Typography sx={{ mb: 0.5, fontWeight: 700 }} fontSize={20}>
                {premium}
                <span style={{ fontSize: '0.75em', fontWeight: 400 }}>
                  {' '}
                  / year
                </span>
              </Typography>
            )}
            <Button
              size='medium'
              variant='contained'
              sx={{
                backgroundColor: getBorderColor(
                  proposal.identifier.toLowerCase(),
                ),
                color: '#fff',
                '&:hover': {
                  backgroundColor: getBorderColor(
                    proposal.identifier.toLowerCase(),
                  ),
                  opacity: 0.85,
                },
              }}
              onClick={async () => {
                setIsSubmitting(true);
                setProposalSelected(true);
                try {
                  await sendEmailWithPremiumProposal({
                    variables: {
                      quoteUuid: quoteUUID ?? '',
                      proposalId: proposal.id ?? '',
                    },
                  });
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              Purchase
            </Button>
          </div>
        </BottomProposalCardSection>
      </StyledProposalCard>
    </>
  );
};

const ProposalModal = ({
  proposal,
  setDisplayProposalModal,
  open,
}: {
  proposal: PremiumProposal;
  setDisplayProposalModal: Dispatch<SetStateAction<boolean>>;
  open: boolean;
}) => {
  return (
    <>
      <Dialog
        open={open}
        aria-labelledby='proposal-dialog'
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Proposal details</DialogTitle>
        <DialogContent>
          <DialogContentText id='business-selection-dialog-description'>
            <Markdown>{proposal.notes}</Markdown>
            <br />
            <Typography variant='h6' sx={{ fontSize: 14 }}>
              Key Policy Details
            </Typography>
            <ul>
              <li>
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                accusantium doloremque laudantium, totam rem aperiam, eaque ipsa
                quae ab illo inventore veritatis et quasi architecto beatae
                vitae dicta sunt explicabo.
              </li>
              <li>
                Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit
                aut fugit, sed quia consequuntur magni dolores eos qui ratione
                voluptatem sequi nesciunt.
              </li>
              <li>
                Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet,
                consectetur, adipisci velit, sed quia non numquam eius modi
                tempora incidunt ut labore et dolore magnam aliquam quaerat
                voluptatem.
              </li>
              <li>
                Ut enim ad minima veniam, quis nostrum exercitationem ullam
                corporis suscipit laboriosam, nisi ut aliquid ex ea commodi
                consequatur?
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={onBack}>Back</Button> */}
          <Button onClick={() => setDisplayProposalModal(false)}>Back</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * TODO move a bunch of the below into a separate utility file
 */
const getBorderColor = (identifier: string) => {
  switch (identifier) {
    case 'gold':
      return '#B59410';
    case 'silver':
      return '#C0C0C0';
    case 'bronze':
      return '#CD7F32';
    default:
      return '#ccc';
  }
};

const BRONZE_DESCRIPTION =
  'Our most affordable option, perfect for individuals or small teams just getting started. Includes essential features to help you meet your goals without breaking the bank.';
const SILVER_DESCRPTION =
  'A balanced choice for growing users who need more flexibility and support. Unlock advanced tools and priority assistance to take your project to the next level.';
const GOLD_DESCPTION =
  'Our most comprehensive package, designed for professionals or businesses who want the best. Full access to all features, premium support, and maximum customization.';
const getDescription = (identifier: string) => {
  switch (identifier.toLowerCase()) {
    case 'gold':
      return GOLD_DESCPTION;
    case 'silver':
      return SILVER_DESCRPTION;
    case 'bronze':
      return BRONZE_DESCRIPTION;
    default:
      return 'Custom proposal.';
  }
};
const StyledProposalCard = styled(Card)({
  minWidth: '20rem',
  maxWidth: '28rem',
  height: '34rem',
  backgroundColor: '#f5f5f5',
  display: 'flex',
  flexDirection: 'column',
  transition: 'box-shadow 0.2s, border-color 0.2s',
});

const BottomProposalCardSection = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const CardInfo = styled(Typography)({
  color: 'text.secondary',
  mb: 0.5,
  fontSize: '0.875rem',
});

const SEND_EMAIL_WITH_PREMIUM_PROPOSAL = graphql(`
  mutation SendEmailWithPremiumProposal($quoteUuid: String!, $proposalId: ID!) {
    sendEmailWithPremiumProposal(
      quoteUuid: $quoteUuid
      proposalId: $proposalId
    ) {
      ok
    }
  }
`);
